export const FAQS = {
  "title": "Frequently Asked Questions",
  "description": "Learn about my engineering background, technical approach, projects, and how I collaborate with teams and clients.",
  "categories": [
    {
      "id": "background",
      "label": "Background & Experience"
    },
    {
      "id": "engineering",
      "label": "Engineering Philosophy"
    },
    {
      "id": "technical",
      "label": "Technical Expertise"
    },
    {
      "id": "projects",
      "label": "Projects & Architecture"
    },
    {
      "id": "collaboration",
      "label": "Collaboration & Delivery"
    },
    {
      "id": "ai",
      "label": "AI-Assisted Development"
    },
    {
      "id": "clients",
      "label": "Working With Me"
    }
  ],
  "faqs": [
    {
      "id": "background-01",
      "category": "background",
      "question": "What kind of software engineer am I?",
      "answer": "I'm a software engineer focused on backend development, distributed systems, cloud infrastructure, and building reliable software. My experience includes developing and maintaining production services, working with long-lived network connections, improving observability, and supporting cloud-based systems. I enjoy understanding how the pieces of a system fit together, not just implementing an isolated feature.",
      "audience": ["employers", "coworkers", "clients"],
      "tags": ["backend", "software-engineering", "distributed-systems"]
    },
    {
      "id": "background-02",
      "category": "background",
      "question": "What is my professional background?",
      "answer": "My background combines software engineering, DevOps, and an academic foundation in software engineering with a minor in marketing. My professional experience has included backend services, cloud environments, production troubleshooting, automation, and developer documentation. This combination helps me consider both the technical requirements of a system and the people who depend on it.",
      "audience": ["employers", "coworkers", "clients"],
      "tags": ["career", "education", "devops"]
    },
    {
      "id": "background-03",
      "category": "background",
      "question": "What did I work on at OpenText?",
      "answer": "At OpenText, I worked on backup software and backend services supporting business customers. My work included C#/.NET services, production reliability, distributed tracing, AWS security controls, monitoring, and authentication-related fixes. I also contributed to onboarding and technical documentation, helping make complex systems easier for other engineers to understand.",
      "audience": ["employers", "coworkers", "clients"],
      "tags": ["opentext", "dotnet", "aws", "observability"]
    },
    {
      "id": "background-04",
      "category": "background",
      "question": "What makes my engineering experience different?",
      "answer": "I've worked with systems where reliability matters beyond whether an individual function returns the correct result. Long-running connections, authentication, service dependencies, telemetry, and production behavior all affect the outcome. I try to understand these interactions and the operational consequences of a change before deciding how to implement it.",
      "audience": ["employers", "coworkers", "clients"],
      "tags": ["reliability", "production-systems", "systems-thinking"]
    },
    {
      "id": "engineering-01",
      "category": "engineering",
      "question": "How do I approach solving a technical problem?",
      "answer": "I start by clarifying the problem, the expected behavior, and the constraints. I then investigate the existing system, identify relevant dependencies and failure cases, and develop a mental model of how the behavior works today. From there, I compare possible solutions, implement the smallest appropriate change, and validate it with tests or other evidence.",
      "audience": ["employers", "coworkers", "clients"],
      "tags": ["problem-solving", "debugging", "design"]
    },
    {
      "id": "engineering-02",
      "category": "engineering",
      "question": "Why do I care about the reason behind a design decision?",
      "answer": "Understanding why a system was designed a certain way helps distinguish an intentional constraint from an accidental complication. Without that context, a seemingly obvious refactor can break hidden assumptions or operational behavior. I prefer to understand the purpose, trade-offs, and boundaries of a design before changing it.",
      "audience": ["employers", "coworkers", "clients"],
      "tags": ["design", "tradeoffs", "maintainability"]
    },
    {
      "id": "engineering-03",
      "category": "engineering",
      "question": "How do I balance clean code with practical delivery?",
      "answer": "I prioritize correctness and the requirements of the task, while looking for opportunities to improve clarity and maintainability. I don't believe every imperfect abstraction needs an immediate rewrite. I prefer focused changes that solve the current problem, preserve existing behavior where required, and leave the system easier to understand rather than introducing unnecessary complexity.",
      "audience": ["employers", "coworkers", "clients"],
      "tags": ["clean-code", "delivery", "maintainability"]
    },
    {
      "id": "engineering-04",
      "category": "engineering",
      "question": "What does maintainable software mean to me?",
      "answer": "Maintainable software is software that another engineer can understand, test, troubleshoot, and safely change without having to rediscover the entire system. That means clear responsibilities, explicit boundaries, useful tests, meaningful names, operational visibility, and documentation that explains important decisions rather than merely repeating the code.",
      "audience": ["employers", "coworkers", "clients"],
      "tags": ["maintainability", "testing", "documentation"]
    },
    {
      "id": "engineering-05",
      "category": "engineering",
      "question": "How do I approach unfamiliar or complicated codebases?",
      "answer": "I start with the system's entry points, key domain concepts, dependencies, and the path through a representative feature. I use documentation, code navigation, tests, and targeted experiments to build an accurate picture before making changes. In large repositories, I also look for ways to reduce irrelevant context so I can concentrate on the specific part of the system I'm trying to understand.",
      "audience": ["employers", "coworkers", "clients"],
      "tags": ["codebases", "learning", "legacy-code"]
    },
    {
      "id": "engineering-06",
      "category": "engineering",
      "question": "How do I approach technical debt?",
      "answer": "I look at the cost and risk of leaving a problem in place, how frequently the affected area changes, and whether the debt makes future work harder or less reliable. I prefer to make technical debt visible, establish clear boundaries, and address it incrementally when possible. Larger refactors should have a clear purpose, a validation strategy, and a reason to justify their risk.",
      "audience": ["employers", "coworkers", "clients"],
      "tags": ["technical-debt", "refactoring", "risk"]
    },
    {
      "id": "technical-01",
      "category": "technical",
      "question": "What programming languages and frameworks do I work with?",
      "answer": "My core backend experience includes C#, .NET, and SQL, alongside JavaScript and TypeScript for application development. I've also worked with Python and Java in project and tooling contexts. The most appropriate language depends on the existing system, the problem being solved, and the capabilities the team needs to maintain.",
      "audience": ["employers", "coworkers", "clients"],
      "tags": ["csharp", "dotnet", "sql", "javascript", "typescript"]
    },
    {
      "id": "technical-02",
      "category": "technical",
      "question": "What is my experience with distributed systems?",
      "answer": "I've worked on backend services that manage large numbers of long-lived TCP connections from backup agents, including authentication, heartbeats, connection state, commands, and events. This experience has shaped how I think about state, failure handling, observability, and the operational behavior of services that remain active for long periods.",
      "audience": ["employers", "coworkers", "clients"],
      "tags": ["distributed-systems", "tcp", "microservices", "networking"]
    },
    {
      "id": "technical-03",
      "category": "technical",
      "question": "What is my experience with AWS and cloud engineering?",
      "answer": "My AWS experience includes API Gateway security controls, AWS WAF rules, CloudWatch alarms, and cloud-based application architecture. I am interested in how infrastructure decisions affect security, reliability, cost, and operational complexity. I approach cloud engineering as a combination of application design, configuration, monitoring, and responsible operation.",
      "audience": ["employers", "coworkers", "clients"],
      "tags": ["aws", "cloud", "security", "monitoring"]
    },
    {
      "id": "technical-04",
      "category": "technical",
      "question": "Why is observability important to me?",
      "answer": "Observability helps engineers understand what a system is doing when its behavior isn't immediately obvious. I've worked on distributed tracing for .NET services using OpenTelemetry, with telemetry flowing through an OpenTelemetry Collector and Data Prepper into OpenSearch for analysis and dashboards. I value telemetry that helps answer practical questions about request flows, dependencies, latency, and failures.",
      "audience": ["employers", "coworkers", "clients"],
      "tags": ["opentelemetry", "distributed-tracing", "opensearch", "observability"]
    },
    {
      "id": "technical-05",
      "category": "technical",
      "question": "How do I think about testing?",
      "answer": "I think tests should verify meaningful behavior and protect the boundaries where failures are most likely or expensive. I start with expected outcomes, then consider edge cases, failure paths, and the seams needed to isolate dependencies. I also care about whether tests are readable and whether they would catch a regression rather than simply exercise code.",
      "audience": ["employers", "coworkers", "clients"],
      "tags": ["testing", "unit-tests", "integration-tests", "quality"]
    },
    {
      "id": "technical-06",
      "category": "technical",
      "question": "How do I approach security in software?",
      "answer": "I treat security as part of the system design rather than a final checklist. My experience includes AWS WAF controls, API Gateway security, and authentication-related fixes. I consider trust boundaries, input validation, authorization, failure behavior, sensitive data exposure, and monitoring, with the specific controls determined by the application's risks and requirements.",
      "audience": ["employers", "coworkers", "clients"],
      "tags": ["security", "authentication", "authorization", "aws"]
    },
    {
      "id": "projects-01",
      "category": "projects",
      "question": "What kinds of projects do I build outside my professional work?",
      "answer": "My personal projects explore backend architecture, cloud engineering, developer tooling, and ways to make technical information more accessible. Examples include a patient vital management system, experiments with AI-connected tools, and Code Lens, a planned code knowledge graph and retrieval tool. I use these projects to investigate architectural trade-offs and turn technical concepts into practical implementations.",
      "audience": ["employers", "coworkers", "clients"],
      "tags": ["personal-projects", "architecture", "developer-tools"]
    },
    {
      "id": "projects-02",
      "category": "projects",
      "question": "What is Code Lens?",
      "answer": "Code Lens is a project exploring how a code knowledge graph can help developers understand an unfamiliar repository. The goal is to represent relationships between code entities and use those relationships to answer questions about code, trace dependencies, and investigate the impact of proposed changes. The initial direction uses a public GitHub repository as input, with future capabilities dependent on implementation and validation.",
      "audience": ["employers", "coworkers", "clients"],
      "tags": ["code-lens", "knowledge-graphs", "graphrag", "developer-tools"]
    },
    {
      "id": "projects-03",
      "category": "projects",
      "question": "Why am I interested in code knowledge graphs?",
      "answer": "A large codebase contains relationships that are difficult to capture through isolated file searches alone. A knowledge graph can model connections between classes, methods, calls, dependencies, and other code entities. I'm interested in whether these relationships can improve code exploration, impact analysis, and retrieval for AI-assisted development, and in how to validate that the resulting answers are grounded in the actual source.",
      "audience": ["employers", "coworkers", "clients"],
      "tags": ["knowledge-graphs", "code-analysis", "graphrag", "ai"]
    },
    {
      "id": "projects-04",
      "category": "projects",
      "question": "What is my approach to building AI-connected tools?",
      "answer": "I focus on the interface between a model and the underlying system: what capabilities are exposed, what context is available, what operations are permitted, and how results can be checked. For example, an MCP server can expose well-defined tools that let an AI model interact with a database or another service. I pay particular attention to permissions, data boundaries, validation, and making tool behavior predictable.",
      "audience": ["employers", "coworkers", "clients"],
      "tags": ["mcp", "ai-tools", "apis", "security"]
    },
    {
      "id": "projects-05",
      "category": "projects",
      "question": "How do I decide whether a technology belongs in a project?",
      "answer": "I start with the problem and its constraints rather than selecting a technology because it is popular. I consider operational overhead, complexity, scale, failure modes, cost, integration effort, and the team's ability to maintain the solution. Sometimes a straightforward database and application are more appropriate than adding a message broker, graph database, or additional service.",
      "audience": ["employers", "coworkers", "clients"],
      "tags": ["architecture", "technology-selection", "tradeoffs"]
    },
    {
      "id": "collaboration-01",
      "category": "collaboration",
      "question": "How do I collaborate with other engineers?",
      "answer": "I value clear technical discussions, explicit assumptions, and shared understanding of how a change affects the system. I try to explain not only what I propose but why, including the trade-offs and alternatives. I also believe knowledge should be shared through useful documentation, code reviews, and discussions that help the team become less dependent on any one person.",
      "audience": ["employers", "coworkers", "clients"],
      "tags": ["teamwork", "code-review", "communication"]
    },
    {
      "id": "collaboration-02",
      "category": "collaboration",
      "question": "How do I document complex systems?",
      "answer": "I aim to document the information that helps someone make a decision or complete a task: system responsibilities, important flows, dependencies, operational procedures, and non-obvious design constraints. I prefer documentation that complements the code and can be maintained alongside it. The goal is to reduce onboarding time and make troubleshooting and future changes less dependent on tribal knowledge.",
      "audience": ["employers", "coworkers", "clients"],
      "tags": ["documentation", "onboarding", "knowledge-sharing"]
    },
    {
      "id": "collaboration-03",
      "category": "collaboration",
      "question": "How do I handle a production issue?",
      "answer": "I focus first on understanding the impact and establishing what is known. I use available logs, metrics, traces, recent changes, and system behavior to narrow down possible causes. I prioritize safe mitigation where necessary, validate hypotheses with evidence, and follow up with tests, documentation, or corrective work that reduces the likelihood of recurrence.",
      "audience": ["employers", "coworkers", "clients"],
      "tags": ["incident-response", "debugging", "reliability"]
    },
    {
      "id": "collaboration-04",
      "category": "collaboration",
      "question": "How do I learn new technologies?",
      "answer": "I prefer to build a conceptual model before diving deeply into implementation details. I learn the problem a technology solves, the abstractions it introduces, its operational behavior, and its limitations. I then reinforce that understanding through focused experiments and practical projects, using documentation and tests to distinguish assumptions from verified behavior.",
      "audience": ["employers", "coworkers", "clients"],
      "tags": ["learning", "technical-growth", "experimentation"]
    },
    {
      "id": "ai-01",
      "category": "ai",
      "question": "How do I use AI in software development?",
      "answer": "I treat AI as an engineering aid rather than a substitute for understanding the system. It can help explore unfamiliar code, draft documentation, generate boilerplate, and accelerate repetitive work. I still want a clear plan, an understanding of the proposed changes, and a way to validate the result. The value comes from combining faster execution with sound engineering judgment.",
      "audience": ["employers", "coworkers", "clients"],
      "tags": ["ai-assisted-development", "copilot", "productivity"]
    },
    {
      "id": "ai-02",
      "category": "ai",
      "question": "Can AI-generated code be trusted without review?",
      "answer": "No. Generated code can satisfy a narrow example while overlooking system invariants, initialization behavior, dependency boundaries, security requirements, or failure cases. I prefer to define the intended behavior, review the proposed design, and validate changes with appropriate tests and code inspection. AI-generated output should meet the same engineering standards as code written by a person.",
      "audience": ["employers", "coworkers", "clients"],
      "tags": ["code-review", "ai", "testing", "reliability"]
    },
    {
      "id": "ai-03",
      "category": "ai",
      "question": "How do I evaluate AI-assisted development tools?",
      "answer": "I look beyond how quickly a tool produces code. I consider whether it understands the relevant context, respects project conventions, produces maintainable changes, and makes its assumptions visible. I also consider test quality, security, context management, and whether the workflow helps developers understand the implementation rather than encouraging unreviewed changes.",
      "audience": ["employers", "coworkers", "clients"],
      "tags": ["ai-tools", "developer-productivity", "evaluation"]
    },
    {
      "id": "clients-01",
      "category": "clients",
      "question": "What kinds of technical problems can I help solve?",
      "answer": "My strongest fit is backend engineering and technical problem-solving involving APIs, service architecture, cloud integrations, observability, automation, and maintainability. I can also explore developer tooling and AI integrations where there is a clear use case. The right engagement depends on the problem, the existing technology stack, the expected deliverables, and the scope of work.",
      "audience": ["clients", "employers"],
      "tags": ["consulting", "backend", "cloud", "apis"]
    },
    {
      "id": "clients-02",
      "category": "clients",
      "question": "How would I approach a new project?",
      "answer": "I would begin by clarifying the business objective, users, expected outcomes, constraints, and definition of success. Next, I would assess the existing environment or outline a suitable architecture, identify the major risks and dependencies, and agree on a practical implementation plan. I prefer incremental delivery, early validation, and clear communication about trade-offs and scope.",
      "audience": ["clients", "employers"],
      "tags": ["project-planning", "requirements", "delivery"]
    },
    {
      "id": "clients-03",
      "category": "clients",
      "question": "How do I balance speed, cost, and reliability?",
      "answer": "I treat them as related engineering constraints rather than independent goals. I first establish the reliability and performance the problem actually requires, then evaluate the simplest solution that meets those requirements. I consider infrastructure and maintenance costs alongside development effort, and I prefer to make important trade-offs explicit instead of optimizing for scale or sophistication without evidence that it is needed.",
      "audience": ["clients", "employers", "coworkers"],
      "tags": ["cost-optimization", "reliability", "architecture"]
    },
    {
      "id": "clients-04",
      "category": "clients",
      "question": "What can people expect when working with me?",
      "answer": "You can expect a thoughtful approach to technical problems, an emphasis on understanding requirements before implementation, and attention to correctness and maintainability. I value transparent communication, evidence-based decisions, and solutions that are understandable by the people who will operate and extend them. I also aim to be clear about assumptions, limitations, and what has or has not been verified.",
      "audience": ["clients", "employers", "coworkers"],
      "tags": ["expectations", "communication", "quality"]
    },
    {
      "id": "clients-05",
      "category": "clients",
      "question": "How can an employer or client learn more about my work?",
      "answer": "You can explore the projects and technical articles on this website to see how I approach software design, cloud engineering, observability, and developer tooling. For a particular project, I can discuss the problem it addresses, the architecture and trade-offs involved, the parts I implemented, and the evidence available to demonstrate its behavior.",
      "audience": ["employers", "clients", "coworkers"],
      "tags": ["portfolio", "projects", "contact"]
    }
  ]
}